// internationalization
import { useTranslation } from 'react-i18next';

// material ui icons
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';

// material ui
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
} from '@mui/material';

// api
import {
  useSearchBuildingQuery,
  useSearchTicketPhotosQuery,
  useSearchTicketTopicsQuery,
} from '@/api/generated/daluxfm/daluxfm';

// relative
import {
  isDaluxBuildingOption,
  isDaluxTopicOption,
} from './forms/daluxTicketForm.mappers';
import EditDaluxTicketForm from './forms/EditDaluxTicketForm';
import type { DaluxAccountId, DaluxTicket } from './types';

type EditDaluxTicketDialogProps = {
  open: boolean;
  onClose: () => void;
  accountId?: DaluxAccountId;
  ticket: DaluxTicket | null;
};

export default function EditDaluxTicketDialog({
  open,
  onClose,
  accountId,
  ticket,
}: EditDaluxTicketDialogProps) {
  const { t } = useTranslation('dalux-integration');
  const ticketId = ticket?.data?.ticketId;
  const { data: topicsData, isLoading: topicsLoading } =
    useSearchTicketTopicsQuery(
      { limit: 100 },
      { skip: !accountId || !open || !ticketId },
    );
  const { data: buildingsData, isLoading: buildingsLoading } =
    useSearchBuildingQuery(
      { limit: 100 },
      { skip: !accountId || !open || !ticketId },
    );
  const { data: photosData, isLoading: photosLoading } =
    useSearchTicketPhotosQuery(
      { ticketId: ticketId || '', limit: 100 },
      { skip: !accountId || !open || !ticketId },
    );
  const topics = (topicsData?.items ?? []).filter(isDaluxTopicOption);
  const buildingOptions = (buildingsData?.items ?? []).filter(
    isDaluxBuildingOption,
  );
  const photos = photosData?.items ?? [];
  const isReady = !topicsLoading && !buildingsLoading && !photosLoading;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <ConfirmationNumberIcon color="primary" />
        {t('ticket.edit_title')}
      </DialogTitle>
      <DialogContent>
        {open && ticket && isReady ? (
          <EditDaluxTicketForm
            key={ticketId}
            ticket={ticket}
            topics={topics}
            buildingOptions={buildingOptions}
            photos={photos}
            onCancel={onClose}
            onSuccess={onClose}
          />
        ) : null}
        {open && ticket && !isReady ? (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress size={24} />
          </Box>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
